import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import Banner, { BannerVariant } from 'components/Banner/Banner';
import ContentWrapper from 'components/ContentWrapper/ContentWrapper';
import MediaUploader, {
  MediaType,
} from 'components/MediaUploader/MediaUploader';
import Panel from 'components/Panel/Panel';
import useDeleteVideo from 'hooks/useDeleteVideo';
import useGetVideoDetails from 'hooks/useGetVideoDetails';
import useNotification from 'hooks/useNotification';
import useUpdateAssessmentVideo, {
  AssessmentVideoType,
} from 'hooks/useUpdateAssessmentVideo';
import { t } from 'locales/i18n';

const WelcomeVideo = () => {
  const { showErrorNotification, showSuccessNotification } = useNotification();
  const { interviewUid = '' } = useParams();
  const [mediaUrl, setMediaUrl] = useState('');
  const [showDeleteBanner, setShowDeleteBanner] = useState(false);

  const { updateAssessmentVideo, isLoading: isLoadingUploadAssessmentVideo } =
    useUpdateAssessmentVideo(interviewUid);
  const { data: getVideoDetails } = useGetVideoDetails({
    interviewUid,
  });
  const { deleteVideo } = useDeleteVideo(interviewUid);

  useEffect(() => {
    if (getVideoDetails?.welcome_videos) {
      setMediaUrl(getVideoDetails?.welcome_videos[0]);
    }
  }, [JSON.stringify(getVideoDetails)]);

  const handleDeleteVideo = async (videoType: AssessmentVideoType) => {
    if (!interviewUid) {
      return;
    }

    await deleteVideo(
      {
        videoType,
      },
      {
        onSuccess: () => {
          showSuccessNotification(
            t('Success'),
            t('Welcome video deleted successfully')
          );

          setMediaUrl('');
          setShowDeleteBanner(true);
          setTimeout(() => {
            setShowDeleteBanner(false);
          }, 60000);
        },
        onError: (error) => {
          showErrorNotification(
            t('An error occurred while deleting the welcome video'),
            error.message
          );
        },
      }
    );
  };

  const handleUploadVideo = async (video: Blob) => {
    await updateAssessmentVideo(
      {
        videoFile: video,
        videoType: AssessmentVideoType.INTRO,
      },
      {
        onSuccess: () => {
          showSuccessNotification(
            t('Success'),
            t('Welcome video uploaded successfully')
          );
        },
        onError: (error) => {
          showErrorNotification(
            t('An error occurred while uploading the welcome video'),
            error.message
          );
        },
      }
    );
  };

  return (
    <ContentWrapper title={t('Welcome Video')}>
      <Panel panelClassName="mt-6 space-y-4">
        {showDeleteBanner && (
          <Banner
            variant={BannerVariant.INFO}
            message={t(
              'Large files may take time to fully delete. If the old video is still visible when a new video is uploaded, please check back in a few minutes.'
            )}
          />
        )}
        <MediaUploader
          acceptFileType=".mp4"
          deleteConfirmation={t('Are you sure you want to delete this video?')}
          deleteDescription={t(
            "Deleting a video is permanent and can't be undone."
          )}
          deleteTitle={t('Delete Video')}
          description={t('Upload an .mp4 file under 200MB')}
          fileSizeLimit={200}
          isLoading={isLoadingUploadAssessmentVideo}
          mediaType={MediaType.VIDEO}
          mediaUrl={mediaUrl}
          onDelete={() => handleDeleteVideo(AssessmentVideoType.INTRO)}
          onUpload={handleUploadVideo}
        />
      </Panel>
    </ContentWrapper>
  );
};

export default WelcomeVideo;
